import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Anfrage der serverseitigen Quelle — Pendant zu `Query().Take(n).RequiresCount()`. */
export interface LoadItemsRequest {
  search: string;
  skip: number;
  take: number;
}

export interface LoadItemsResult<TItem> {
  items: TItem[];
  count: number;
}

export interface DropDownItemsOptions<TItem> {
  open: boolean;
  /** Clientseitige Quelle. */
  items?: TItem[];
  /** Serverseitige Quelle mit Suche und Nachladen. */
  loadItems?: (request: LoadItemsRequest) => Promise<LoadItemsResult<TItem>>;
  textField: keyof TItem & string;
  pageSize: number;
}

export interface DropDownItemsState<TItem> {
  visibleItems: TItem[];
  filter: string;
  setFilter: (text: string) => void;
  /** Lädt die nächste Seite, solange noch nicht alle Einträge geladen sind. */
  loadMore: () => void;
  loading: boolean;
}

/**
 * Beschafft die Einträge einer Auswahlliste. Bei `loadItems` wird seitenweise
 * nachgeladen (Ersatz für `EnableVirtualization` + `SfDataManager`), sonst
 * clientseitig gefiltert.
 */
export function useDropDownItems<TItem>(
  options: DropDownItemsOptions<TItem>,
): DropDownItemsState<TItem> {
  const { open, items, loadItems, textField, pageSize } = options;

  const [filter, setFilter] = useState('');
  const [loaded, setLoaded] = useState<TItem[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // Verwirft Antworten überholter Anfragen.
  const requestId = useRef(0);

  const fetchPage = useCallback(
    async (search: string, skip: number) => {
      if (loadItems === undefined) return;

      requestId.current += 1;
      const current = requestId.current;
      setLoading(true);
      try {
        const result = await loadItems({ search, skip, take: pageSize });
        if (current !== requestId.current) return;
        setLoaded((previous) => (skip === 0 ? result.items : [...previous, ...result.items]));
        setCount(result.count);
      } finally {
        if (current === requestId.current) setLoading(false);
      }
    },
    [loadItems, pageSize],
  );

  useEffect(() => {
    if (!open) {
      setFilter('');
      return;
    }
    void fetchPage(filter, 0);
  }, [fetchPage, filter, open]);

  const loadMore = useCallback(() => {
    if (loadItems === undefined || loading || loaded.length >= count) return;
    void fetchPage(filter, loaded.length);
  }, [count, fetchPage, filter, loadItems, loaded.length, loading]);

  const visibleItems = useMemo(() => {
    if (loadItems !== undefined) return loaded;

    const source = items ?? [];
    const needle = filter.trim().toLowerCase();
    if (needle === '') return source;
    return source.filter((item) =>
      String(item[textField] ?? '')
        .toLowerCase()
        .includes(needle),
    );
  }, [filter, items, loadItems, loaded, textField]);

  return { visibleItems, filter, setFilter, loadMore, loading };
}
